import { stripLocalePathPrefix } from '@heidi/utils/i18n';

// RUM records full resource/view URLs, and several auth flows carry bearer
// tokens in the query string, the hash, a path segment, or a Daytona preview
// hostname (`{port}-{token}.daytonaproxy01.net`), so redact those before events are
// sent. Keep the query-param list in sync with the ml-scribe backend redaction.
export const SENSITIVE_QUERY_PARAMS = [
  'token',
  'access_token',
  'launch_token',
  'refresh_token',
  'id_token',
  'code',
  'state',
  // Auth0's signed redirect token for the hosted onboarding gate.
  'session_token',
  'patient_identifier',
  // Work Home object workspace: `objectId` is a patient/visit identifier in
  // the page URL (`?objectKind=patient&objectId=…`). Matched folded, so
  // `objectId` / `ObjectId` both redact. `sourceSessionId` stays — it is the
  // pseudonymous scribe session id used to restore identity without PHI.
  'objectid',
  // Heidi's own launch params: `t` is the widget auth token (query on
  // /integration/widget/auth, hash on the legacy /integration/auth), sgimed
  // passes a compact JWT as `context`, and medtech signs its launch with
  // `context` + `signature`.
  't',
  'context',
  'signature',
  'x-amz-signature',
  'x-amz-credential',
  'x-amz-security-token',
  'x-amz-signedheaders',
  'x-amz-algorithm',
  'x-amz-date',
  'x-amz-expires',
  'expires',
  'key-pair-id',
  'awsaccesskeyid',
  // Typeahead free text: PHI that lands in RUM resource URLs.
  'query',
  // Team invite links name the invited address in the page URL.
  'email',
] as const;

const REDACTED = 'REDACTED';

// sgimed reads both `context` and `Context`, so match on a folded key.
const SENSITIVE_PARAMS = new Set<string>(SENSITIVE_QUERY_PARAMS);

// Pages whose query string is passed through to the backend as EHR filters
// (`/integration/appointment-launch?encounter-number=<n>`): the FE does not know
// the keys, so every value is treated as a patient identifier.
const REDACT_ALL_QUERY_PATHS = new Set(['/integration/appointment-launch']);

// Params whose value is itself a post-login URL, so its own query is redacted.
const NESTED_REDIRECT_PARAMS = new Set(['returnto', 'post_login_redirect_url']);

function isRedactAllQueryPath(pathOrUrl: string): boolean {
  const pathname = pathOrUrl.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/?#]*/i, '');
  const path = stripLocalePathPrefix(pathname).replace(/\/+$/, '') || '/';
  return REDACT_ALL_QUERY_PATHS.has(path);
}

// A name list only covers the params we already know about. Redact anything
// shaped like a JWT whatever it is called, so a new launch param does not
// silently start leaking.
const JWT_SHAPE = /^eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]*$/;

function decode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

// The legacy widget hash encodes values as `t="<token>"`.
function isQuoted(value: string): boolean {
  return value.length >= 2 && value.startsWith('"') && value.endsWith('"');
}

function isSensitive(rawKey: string, rawValue: string): boolean {
  if (SENSITIVE_PARAMS.has(decode(rawKey).toLowerCase())) return true;

  const value = decode(rawValue);
  return JWT_SHAPE.test(isQuoted(value) ? value.slice(1, -1) : value);
}

/**
 * Auth0 `returnTo` embeds the post-login path (often with `?context=` / OAuth
 * params). Top-level pair redaction never sees those nested keys — peel the
 * query out of the decoded path and run the same pair rules on it.
 */
function redactNestedReturnTo(rawValue: string): string | null {
  const decoded = decode(rawValue);
  const queryIndex = decoded.indexOf('?');
  const path = queryIndex === -1 ? decoded : decoded.slice(0, queryIndex);
  const query = queryIndex === -1 ? null : decoded.slice(queryIndex + 1);

  // The nested path can be a token-bearing patient link too.
  const redactedPath = redactPathSegments(path);
  const nestedRedacted =
    query === null ? null : redactPairs(query, isRedactAllQueryPath(path));
  if (redactedPath === null && nestedRedacted === null) return null;

  const nestedQuery = query === null ? '' : `?${nestedRedacted ?? query}`;
  return encodeURIComponent(`${redactedPath ?? path}${nestedQuery}`);
}

// Masks values in an `&`-separated pair list, leaving every other byte alone.
// Returns null when nothing matched, so callers can hand back the input
// unchanged rather than a re-serialised equivalent.
function redactPairs(pairs: string, redactAll = false): string | null {
  let mutated = false;

  const redacted = pairs.split('&').map((segment) => {
    const eqIndex = segment.indexOf('=');
    if (eqIndex === -1) return segment;

    const key = segment.slice(0, eqIndex);
    const value = segment.slice(eqIndex + 1);
    if (!value) return segment;

    if (!redactAll && NESTED_REDIRECT_PARAMS.has(decode(key).toLowerCase())) {
      const nested = redactNestedReturnTo(value);
      if (nested !== null) {
        mutated = true;
        return `${key}=${nested}`;
      }
    }

    if (!redactAll && !isSensitive(key, value)) return segment;

    mutated = true;
    return `${key}=${isQuoted(value) ? `"${REDACTED}"` : REDACTED}`;
  });

  return mutated ? redacted.join('&') : null;
}

// Daytona signed preview URLs encode the bearer token in the hostname
// (`https://{port}-{token}.daytonaproxy01.net/vnc.html`). Query/hash
// redaction never sees that form. Collapse the host so RUM resource timing
// stays and the token does not.
const DAYTONA_HOST_SUFFIXES = ['.daytonaproxy01.net', '.proxy.daytona.work'] as const;

function redactDaytonaHostname(url: string): string | null {
  const schemeEnd = url.indexOf('://');
  if (schemeEnd === -1) return null;

  const hostStart = schemeEnd + 3;
  let hostEnd = hostStart;
  while (hostEnd < url.length) {
    const ch = url[hostEnd];
    if (ch === '/' || ch === '?' || ch === '#') break;
    hostEnd += 1;
  }

  const host = url.slice(hostStart, hostEnd);
  if (!host) return null;

  const colon = host.lastIndexOf(':');
  const hostname = colon === -1 ? host : host.slice(0, colon);
  const port = colon === -1 ? '' : host.slice(colon);

  const suffix = DAYTONA_HOST_SUFFIXES.find(
    (candidate) => hostname === candidate.slice(1) || hostname.endsWith(candidate)
  );
  if (!suffix) return null;

  const redactedHost = `redacted${suffix}`;
  if (hostname === redactedHost) return null;

  return `${url.slice(0, hostStart)}${redactedHost}${port}${url.slice(hostEnd)}`;
}

// Routes that carry a bearer link token as a path segment, so the query and
// hash rules never see it: the patient pre-consult page and its verify call.
const SENSITIVE_PATH_SEGMENTS = [
  /(\/patient-sessions\/pre-consult\/)[^/?#]+/,
  /(\/public\/pre-consults\/)[^/?#:]+(?=:verify)/,
] as const;

function redactPathSegments(url: string): string | null {
  const pathEnd = url.search(/[?#]/);
  const path = pathEnd === -1 ? url : url.slice(0, pathEnd);
  const redacted = SENSITIVE_PATH_SEGMENTS.reduce(
    (current, pattern) => current.replace(pattern, `$1${REDACTED}`),
    path
  );
  if (redacted === path) return null;
  return pathEnd === -1 ? redacted : `${redacted}${url.slice(pathEnd)}`;
}

export function redactSensitiveUrlParams(url: string): string {
  if (typeof url !== 'string') return url;

  const hostRedacted = redactDaytonaHostname(url);
  const pathInput = hostRedacted ?? url;
  const working = redactPathSegments(pathInput) ?? pathInput;
  if (!working.includes('?') && !working.includes('#')) return working;

  const hashIndex = working.indexOf('#');
  const head = hashIndex >= 0 ? working.slice(0, hashIndex) : working;
  const fragment = hashIndex >= 0 ? working.slice(hashIndex + 1) : null;

  let mutated = false;

  let redactedHead = head;
  const queryIndex = head.indexOf('?');
  if (queryIndex >= 0) {
    const redactedQuery = redactPairs(
      head.slice(queryIndex + 1),
      isRedactAllQueryPath(head.slice(0, queryIndex))
    );
    if (redactedQuery !== null) {
      redactedHead = `${head.slice(0, queryIndex + 1)}${redactedQuery}`;
      mutated = true;
    }
  }

  let redactedFragment = fragment;
  if (fragment) {
    // A hash-router fragment carries its own query (`#/callback?token=…`);
    // otherwise the whole fragment is the pair list (`#t="…"&region="AU"`).
    const fragmentQueryIndex = fragment.indexOf('?');
    const path = fragmentQueryIndex >= 0 ? fragment.slice(0, fragmentQueryIndex + 1) : '';
    const pairs =
      fragmentQueryIndex >= 0 ? fragment.slice(fragmentQueryIndex + 1) : fragment;

    const redactedPairs = redactPairs(pairs);
    if (redactedPairs !== null) {
      redactedFragment = `${path}${redactedPairs}`;
      mutated = true;
    }
  }

  if (!mutated) return working;
  return redactedFragment === null ? redactedHead : `${redactedHead}#${redactedFragment}`;
}

// Redacts every URL field a RUM event can carry, in place.
export function redactRumEventUrls(event: {
  resource?: { url?: string };
  view?: { url?: string; referrer?: string };
  error?: { resource?: { url?: string } };
}): void {
  if (event.resource?.url) {
    event.resource.url = redactSensitiveUrlParams(event.resource.url);
  }
  if (event.error?.resource?.url) {
    event.error.resource.url = redactSensitiveUrlParams(event.error.resource.url);
  }
  if (event.view?.url) {
    event.view.url = redactSensitiveUrlParams(event.view.url);
  }
  if (event.view?.referrer) {
    event.view.referrer = redactSensitiveUrlParams(event.view.referrer);
  }
}
